import React, { ComponentProps, useMemo, createElement, useEffect } from 'react';
import { usePreferences } from '@context/preferences.context';
import dynamic from 'next/dynamic';
import { useBucket } from '@context/bucket.context';
import { PayPalOSMService } from '@components/bucketTest/paypal/paypal.service';
import { KlarnaOSMService } from '@components/bucketTest/klarna/klarna.service';
import ProductInstallment from '@components/molecules/products/product-details/components/product-installment/product-installment';
import { useBucketTestEvents } from '@hooks/bucket-test/use-bucket-test-events';

const PaypalOSM = dynamic(() => import('@components/bucketTest/paypal/paypal.osm'), { ssr: false });
const KlarnaOSM = dynamic(() => import('@components/bucketTest/klarna/klarna.osm'), { ssr: false });

type BucketTestProductInstallmentProps = Omit<ComponentProps<typeof ProductInstallment>, 'children'>;

const BucketTestProductInstallment: React.FC<BucketTestProductInstallmentProps> = ({ product }) => {
  const { isFeatureEnabled } = useBucket();
  const { sendBNPLShowEvent: sendBNPLShowNothing } = useBucketTestEvents('');
  const { sendBNPLShowEvent: sendBNPLShowEventPayPal } = useBucketTestEvents('paypal');
  const { sendBNPLShowEvent: sendBNPLShowEventKlarna } = useBucketTestEvents('klarna');
  const paypalConfigs = useMemo(() => PayPalOSMService.getConfigs(), []);
  const { currency, language: rawLanguage, country } = usePreferences();
  const language = rawLanguage?.toLocaleLowerCase() === 'us' ? 'en' : rawLanguage;
  const klarnaConfig = useMemo(() => KlarnaOSMService.getConfigs({ language, country, currency }), []);
  const paypalIsEligible = useMemo(() => PayPalOSMService.isEligible({ language, country, currency }), []);
  const isPayPalEnabled = isFeatureEnabled((f) => f.productBnplMessageType === 'paypal');
  const isKlarnaEnabled = isFeatureEnabled((f) => f.productBnplMessageType === 'klarna');
  const isCurrentEnabled = isFeatureEnabled((f) => f.productBnplMessageType === 'current');
  const isNoneEnabled = isFeatureEnabled((f) => f.productBnplMessageType === 'none');

  useEffect(() => {
    if (isPayPalEnabled && !paypalIsEligible) {
      sendBNPLShowEventPayPal('control');
    }
    if (isKlarnaEnabled && !klarnaConfig) {
      sendBNPLShowEventKlarna('control');
    }
    if (isNoneEnabled) {
      sendBNPLShowNothing('control');
    }
  }, [isPayPalEnabled, isKlarnaEnabled, isNoneEnabled]);

  if (paypalIsEligible && isPayPalEnabled) {
    return createElement(PaypalOSM, {
      amount: product?.price.cents / 100,
      buyerCountry: country,
      configs: {
        ...paypalConfigs,
      },
    });
  }
  if (klarnaConfig && isKlarnaEnabled) {
    return createElement(KlarnaOSM, {
      // TODO: Can this just be locale from usePreferences?
      locale: `${language}-${country}`,
      purchaseAmount: product?.price.cents,
      configs: klarnaConfig,
    });
  }
  if (isCurrentEnabled) {
    return createElement(ProductInstallment, {
      product,
    });
  }
  return null;
};

export default BucketTestProductInstallment;
